import { useContext, useState } from 'react'
import { fetchSpotifyArtist } from 'shared/api'
import UserContext from 'shared/contexts/user'
import TextFieldAsync from 'shared/components/edit-panel/info/textfield-async'
import { isSpotifyDeepLinkUrl } from 'shared/util/gif-maker-util'
import { CreationToolsContext } from '../../../manager'
import CreationToolOutputType from '../../../types/output-type'
import CreationToolUploadField from '../../../types/upload-field'
import { UploadFieldBlock } from '../../blocks'
import { CreationToolUploadContext } from '../../upload/manager'
import CTA from './cta'
import Instructions from './field-instructions'
import Title from './field-title'

type Props = {
    className?: string
    field: CreationToolUploadField
    sourceUrl?: string
    showInstructions?: boolean
    ignoreCTA?: boolean
    onChange: (sourceUrl: string) => void
}

const CreationSourceURLField = ({
    className,
    field,
    sourceUrl = '',
    showInstructions = false,
    ignoreCTA = false,
    onChange,
}: Props) => {
    const placeholder = CreationToolUploadField.description(field)
    const maxLength = CreationToolUploadField.maxCharacterCount(field)
    const { user } = useContext(UserContext)
    const { setCtaText } = useContext(CreationToolUploadContext)
    const { outputType } = useContext(CreationToolsContext)

    const [isSpotify, setIsSpotify] = useState(false)
    const [toggleOn, setToggleOn] = useState(false)
    const [hasChanged, setHasChanged] = useState(false)
    const [name, setName] = useState('')

    const showCTA =
        !ignoreCTA && user.isStaff && sourceUrl !== '' && isSpotify && outputType !== CreationToolOutputType.Video

    const validateAndUpdateSourceURL = (url: string) => {
        setToggleOn(false)

        if (isSpotifyDeepLinkUrl(url)) {
            setIsSpotify(true)
        } else {
            setIsSpotify(false)
        }

        setHasChanged(true)
        !ignoreCTA && setCtaText('')
        onChange(url)
    }

    const toggleCTA = async (isOn: boolean) => {
        setToggleOn(isOn)
        if (isOn && isSpotify) {
            if (hasChanged) {
                const artistName = await fetchSpotifyArtist(sourceUrl)
                setName(artistName)
                setCtaText(`Listen to ${artistName} on Spotify`)
            } else {
                setCtaText(`Listen to ${name} on Spotify`)
            }
        } else {
            setCtaText('')
        }
        setHasChanged(false)
    }

    return (
        <UploadFieldBlock className={className}>
            <Title field={field} />
            <TextFieldAsync
                value={sourceUrl}
                title={'Source URL'}
                isURL={true}
                placeholder={placeholder}
                maxLength={maxLength}
                onChange={(ctaUrl: string) => validateAndUpdateSourceURL(ctaUrl)}
                includeTitle={false}
            />
            {showInstructions && <Instructions field={field} />}
            {showCTA && <CTA isOn={toggleOn} onToggle={() => toggleCTA(!toggleOn)} />}
        </UploadFieldBlock>
    )
}

export default CreationSourceURLField
